import { helpApi } from "@/services/api/help";
import { getAccessToken } from "@/utils/tokenStorage";

export type SttResult = { text: string; final: boolean };

export type TalkStt = {
  send: (pcm: ArrayBuffer) => void;
  finish: () => Promise<SttResult>;
  pursue: () => Promise<string>;
  close: () => void;
};

export type SttPiece = {
  text?: string;
  final?: boolean;
  speechFinal?: boolean;
};

const DONE_WAIT_MS = 4000;

export function mergeHeard(locked: string, text: string): string {
  if (!text) return locked;
  if (!locked || text.startsWith(locked)) return text;
  if (locked.endsWith(text) || locked.startsWith(text)) return locked;
  return `${locked} ${text}`;
}

export function applySttPiece(
  locked: string,
  tail: string,
  msg: SttPiece
): { locked: string; tail: string } {
  void tail;
  const text = (msg.text || "").trim();
  if (msg.speechFinal || msg.final)
    return { locked: mergeHeard(locked, text), tail: "" };
  return { locked, tail: text };
}

export function sttHeard(locked: string, tail: string): string {
  return [locked, tail].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
}

function socketUrl(language: string, ticket: string): string {
  const path = `/api/v1/help/stt-live?ticket=${encodeURIComponent(ticket)}&language=${encodeURIComponent(language)}`;
  const base = import.meta.env.VITE_API_URL || "";
  if (base) {
    const url = new URL(path, base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  }
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${path}`;
}

export function shouldWaitForTranscript(
  ready: boolean,
  sentAudio: boolean
): boolean {
  return ready && sentAudio;
}

export function openTalkStt(
  language: string,
  onPartial?: (text: string) => void
): TalkStt | null {
  if (!getAccessToken() || typeof WebSocket === "undefined") return null;
  const pending: ArrayBuffer[] = [];
  let ws: WebSocket | null = null;
  let ready = false;
  let sentAudio = false;
  let released = false;
  let closed = false;
  let locked = "";
  let tail = "";
  let done = "";
  let settled = false;
  let resolveDone: (result: SttResult) => void = () => {};
  let resolvePursue: (text: string) => void = () => {};
  const donePromise = new Promise<SttResult>((resolve) => {
    resolveDone = resolve;
  });
  const pursuePromise = new Promise<string>((resolve) => {
    resolvePursue = resolve;
  });

  const settle = (text: string, final = false) => {
    const heard = text.trim();
    resolvePursue(heard);
    if (settled) return;
    settled = true;
    resolveDone({ text: final ? heard : "", final: final && Boolean(heard) });
  };

  const flush = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    for (const buf of pending.splice(0)) ws.send(buf);
  };

  const finalize = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "Finalize" }));
    ws.send(JSON.stringify({ type: "audio.done" }));
  };

  const onMessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") return;
    try {
      const msg = JSON.parse(event.data) as SttPiece & { type?: string };
      if (msg.type === "ready") {
        ready = true;
        flush();
        if (released && sentAudio) finalize();
        return;
      }
      if (msg.type === "partial") {
        const next = applySttPiece(locked, tail, msg);
        locked = next.locked;
        tail = next.tail;
        onPartial?.(sttHeard(locked, tail));
      }
      if (msg.type === "done") {
        done = (msg.text || "").trim() || sttHeard(locked, tail);
        settle(done, true);
      }
      if (msg.type === "error") settle("");
    } catch {
      /* a bad frame must not end the utterance */
    }
  };
  const wire = (socket: WebSocket) => {
    socket.binaryType = "arraybuffer";
    socket.onopen = () => flush();
    socket.onmessage = onMessage;
    socket.onerror = () => settle(done, Boolean(done));
    socket.onclose = () => settle(done, Boolean(done));
  };

  void helpApi
    .sttTicket()
    .then(({ ticket }) => {
      if (closed || settled) return;
      ws = new WebSocket(socketUrl(language, ticket));
      wire(ws);
    })
    .catch(() => settle(""));

  return {
    send(pcm) {
      if (!pcm.byteLength || closed) return;
      sentAudio = true;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(pcm);
      else pending.push(pcm);
    },
    finish() {
      released = true;
      if (!sentAudio) {
        closed = true;
        settle("");
        try {
          ws?.close();
        } catch {
          /* ignore */
        }
        return Promise.resolve({ text: "", final: false });
      }
      if (ready && ws && ws.readyState === WebSocket.OPEN) finalize();
      const waitMs = ready ? DONE_WAIT_MS : 12_000;
      return Promise.race([
        donePromise,
        new Promise<SttResult>((resolve) =>
          window.setTimeout(() => resolve({ text: "", final: false }), waitMs)
        ),
      ]);
    },
    pursue() {
      return Promise.race([
        pursuePromise,
        new Promise<string>((resolve) =>
          window.setTimeout(() => resolve(""), 8000)
        ),
      ]);
    },
    close() {
      closed = true;
      settle(done, Boolean(done));
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    },
  };
}
